import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { AlertTriangle, Check, Lock, ArrowUpCircle, X, ShieldAlert, Search } from 'lucide-react';

// Nombres legibles de los campos que se pueden comparar entre original y duplicada
const CAMPOS_COMPARABLES = [
  { campo: 'domicilio_breve', etiqueta: 'Domicilio' },
  { campo: 'calle', etiqueta: 'Calle' },
  { campo: 'colonia', etiqueta: 'Colonia' },
  { campo: 'codigo_postal', etiqueta: 'Código Postal' },
  { campo: 'telefono_alterno', etiqueta: 'Teléfono Alterno' },
  { campo: 'ocupacion_general', etiqueta: 'Ocupación' },
  { campo: 'profesion_corta', etiqueta: 'Profesión' },
  { campo: 'nivel_estudios', etiqueta: 'Nivel de Estudios' },
  { campo: 'disponibilidad_servir', etiqueta: 'Disponibilidad' },
  { campo: 'area_interes', etiqueta: 'Área de Interés' },
  { campo: 'observaciones', etiqueta: 'Observaciones' },
];

// Cuenta cuántos de esos campos vienen realmente llenos
function contarCamposLlenos(datos) {
  if (!datos) return 0;
  return CAMPOS_COMPARABLES.filter(c => datos[c.campo] && String(datos[c.campo]).trim() !== '').length;
}

export default function Duplicidades() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ROLES_DUPLICIDADES = ["superadmin","coordinador_general","coordinador_territorial","coordinador_sectorial","seccional","auditor"];
  const puedeAcceder = ROLES_DUPLICIDADES.includes(user?.rol);
  const puedeSubirNivel = user?.rol === "superadmin";
  const puedeConflictos = ["superadmin","coordinador_general"].includes(user?.rol);

  const [pestana, setPestana] = useState('duplicidades');
  const [duplicidades, setDuplicidades] = useState([]);
  const [conflictos, setConflictos] = useState([]);
  const [statsConflictos, setStatsConflictos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingConflictos, setLoadingConflictos] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [duplicidadSeleccionada, setDuplicidadSeleccionada] = useState(null);
  const [mostrarSubirNivel, setMostrarSubirNivel] = useState(false);
  const [dupParaSubir, setDupParaSubir] = useState(null);
  const [estatusTalento, setEstatusTalento] = useState('por_ampliar');
  const [resultadoSubida, setResultadoSubida] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [detectando, setDetectando] = useState(false);
  const [filtroConflicto, setFiltroConflicto] = useState('');
  const [conflictoSeleccionado, setConflictoSeleccionado] = useState(null);
  const [resolucion, setResolucion] = useState('');

  useEffect(() => {
    if (puedeAcceder) fetchDuplicidades();
    else setLoading(false);
  }, [puedeAcceder]);

  useEffect(() => {
    if (pestana === 'conflictos' && puedeConflictos) fetchConflictos();
  }, [pestana]);

  const fetchDuplicidades = async () => {
    try {
      const response = await api.get('/capturas/duplicidades?estatus=pendiente');
      setDuplicidades(response.data.duplicidades);
    } catch (error) {
      console.error('Error al cargar duplicidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConflictos = async () => {
    setLoadingConflictos(true);
    try {
      const res = await api.get('/conflictos');
      setConflictos(res.data.conflictos || []);
      setStatsConflictos(res.data.stats || null);
    } catch (error) {
      console.error('Error al cargar conflictos:', error);
    } finally {
      setLoadingConflictos(false);
    }
  };

  const handleDetectarConflictos = async () => {
    setDetectando(true);
    try {
      const res = await api.post('/conflictos/detectar');
      setMensaje({ tipo: 'exito', texto: res.data.mensaje });
      fetchConflictos();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al detectar conflictos.' });
    } finally {
      setDetectando(false);
    }
  };

  const handleResolverConflicto = async (id, estructuraGanadora) => {
    try {
      await api.put(`/conflictos/${id}`, {
        estatus: 'resuelto',
        resolucion_final: resolucion,
        estructura_ganadora: estructuraGanadora
      });
      setMensaje({ tipo: 'exito', texto: 'Conflicto resuelto correctamente — se asignó la estructura ganadora.' });
      setConflictoSeleccionado(null);
      setResolucion('');
      fetchConflictos();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al resolver conflicto.' });
    }
  };

  const handleResolver = async (duplicidadId, resolucionDup) => {
    try {
      await api.put(`/capturas/duplicidades/${duplicidadId}`, { resolucion: resolucionDup, estatus: 'resuelto' });
      setMensaje({ tipo: 'exito', texto: 'Duplicidad resuelta correctamente.' });
      setDuplicidadSeleccionada(null);
      fetchDuplicidades();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al resolver duplicidad.' });
    }
  };

  const abrirSubirNivel = (dup) => {
    setDupParaSubir(dup);
    setEstatusTalento('por_ampliar');
    setResultadoSubida(null);
    setMostrarSubirNivel(true);
    setDuplicidadSeleccionada(null);
  };

  const handleSubirNivel = async () => {
    if (!dupParaSubir) return;
    setProcesando(true);
    try {
      const res = await api.post(`/capturas/duplicidades/${dupParaSubir.id}/subir-nivel`, {
        estatus_talento: estatusTalento
      });
      setResultadoSubida(res.data);
      fetchDuplicidades();
    } catch (error) {
      setResultadoSubida({ error: error.response?.data?.error || 'Error al subir de nivel.' });
    } finally {
      setProcesando(false);
    }
  };

  const getPrioridadColor = (p) => {
    if (p === 'alta') return 'bg-red-100 text-red-800';
    if (p === 'media') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-700';
  };

  const getEstatusConflictoColor = (e) => {
    if (e === 'pendiente') return 'bg-orange-100 text-orange-800';
    if (e === 'en_revision') return 'bg-blue-100 text-blue-800';
    if (e === 'resuelto') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-700';
  };

  const conflictosFiltrados = conflictos.filter(c =>
    filtroConflicto ? c.estatus === filtroConflicto : c.estatus !== 'resuelto'
  );

  if (!puedeAcceder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-10" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            ← Volver al Dashboard
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-10" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-6xl mx-auto">

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="text-orange-500" size={28} />
            Duplicidades y Conflictos
          </h2>
          <p className="text-gray-500 text-sm">Revisión y resolución de registros duplicados y disputas políticas</p>
        </div>

        {/* PESTAÑAS */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button onClick={() => { setPestana('duplicidades'); setMensaje(null); }}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition ${
              pestana === 'duplicidades'
                ? 'bg-white border border-b-white border-gray-200 text-orange-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            ⚠️ Duplicidades
            {duplicidades.length > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {duplicidades.length}
              </span>
            )}
          </button>
          {puedeConflictos && (
            <button onClick={() => { setPestana('conflictos'); setMensaje(null); }}
              className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition ${
                pestana === 'conflictos'
                  ? 'bg-white border border-b-white border-gray-200 text-red-600 -mb-px'
                  : 'text-gray-500 hover:text-gray-700'
              }`}>
              🛡️ Conflictos Políticos
              {statsConflictos?.pendientes > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {statsConflictos.pendientes}
                </span>
              )}
            </button>
          )}
        </div>

        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {mensaje.texto}
          </div>
        )}

        {/* ── PESTAÑA DUPLICIDADES ── */}
        {pestana === 'duplicidades' && (
          <div className="space-y-4">
            {loading ? (
              <p className="text-gray-500 text-center py-8">Cargando duplicidades...</p>
            ) : duplicidades.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <Check className="text-green-600 mx-auto mb-2" size={32} />
                <p className="text-green-800 font-medium">✅ ¡No hay duplicidades pendientes!</p>
                <p className="text-green-600 text-sm mt-1">Todos los registros han sido validados</p>
              </div>
            ) : (
              duplicidades.map((dup, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-400">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">{dup.persona}</h3>
                      <p className="text-sm text-gray-500">
                        Teléfono: {dup.telefono_principal} | Clave: {dup.clave_elector}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                      {dup.tipo_duplicidad}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                      <p className="text-xs text-green-700 font-medium mb-1">🥇 Capturó primero (Original)</p>
                      <p className="text-sm font-semibold text-gray-800">{dup.original_capturado_por || 'No disponible'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {dup.original_seccion ? `Sección ${dup.original_seccion}` : ''}
                        {dup.original_fecha ? ` — ${new Date(dup.original_fecha).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}
                      </p>
                      <p className="text-xs font-medium text-green-700 mt-2">
                        📋 {contarCamposLlenos(dup.original_datos)} de {CAMPOS_COMPARABLES.length} datos llenos
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                      <p className="text-xs text-blue-700 font-medium mb-1">🥈 Capturó después (Duplicada)</p>
                      <p className="text-sm font-semibold text-gray-800">{dup.duplicada_capturado_por || 'No disponible'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {dup.duplicada_seccion ? `Sección ${dup.duplicada_seccion}` : ''}
                        {dup.duplicada_fecha ? ` — ${new Date(dup.duplicada_fecha).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}
                      </p>
                      <p className="text-xs font-medium text-blue-700 mt-2">
                        📋 {contarCamposLlenos(dup.duplicada_datos)} de {CAMPOS_COMPARABLES.length} datos llenos
                      </p>
                    </div>
                  </div>

                  {(dup.original_datos || dup.duplicada_datos) && (
                    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Campo</th>
                            <th className="text-left py-2 px-3 font-medium text-green-700">Original</th>
                            <th className="text-left py-2 px-3 font-medium text-blue-700">Duplicada</th>
                          </tr>
                        </thead>
                        <tbody>
                          {CAMPOS_COMPARABLES.filter(c =>
                            (dup.original_datos && dup.original_datos[c.campo]) ||
                            (dup.duplicada_datos && dup.duplicada_datos[c.campo])
                          ).map(c => (
                            <tr key={c.campo} className="border-t border-gray-100">
                              <td className="py-1.5 px-3 text-gray-500">{c.etiqueta}</td>
                              <td className="py-1.5 px-3 text-gray-800">{dup.original_datos?.[c.campo] || '—'}</td>
                              <td className="py-1.5 px-3 text-gray-800">{dup.duplicada_datos?.[c.campo] || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="bg-orange-50 p-3 rounded-lg mb-4 pb-4 border-b">
                    <p className="text-xs text-gray-500 mb-1">Resolución sugerida</p>
                    <p className="text-sm text-gray-700">{dup.resolucion}</p>
                  </div>

                  {duplicidadSeleccionada === dup.id ? (
                    <div className="space-y-2">
                      <button onClick={() => handleResolver(dup.id, 'original')}
                        className="w-full text-left p-3 border border-green-200 bg-green-50 hover:bg-green-100 rounded-lg transition">
                        <p className="font-medium text-green-800">✅ Mantener la original</p>
                        <p className="text-xs text-green-600">Se eliminará el duplicado</p>
                      </button>
                      <button onClick={() => handleResolver(dup.id, 'duplicada')}
                        className="w-full text-left p-3 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg transition">
                        <p className="font-medium text-blue-800">🔄 Usar la duplicada</p>
                        <p className="text-xs text-blue-600">Se reemplazará la original</p>
                      </button>
                      {puedeSubirNivel && (
                        <button onClick={() => abrirSubirNivel(dup)}
                          className="w-full text-left p-3 border-2 border-purple-300 bg-purple-50 hover:bg-purple-100 rounded-lg transition">
                          <p className="font-medium text-purple-800 flex items-center gap-2">
                            <ArrowUpCircle size={16} /> ⬆️ Subir de nivel — Convertir en Promotor
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            Crea su usuario como Promotor, lo agrega al Banco de Talento y resuelve el duplicado
                          </p>
                        </button>
                      )}
                      <button onClick={() => setDuplicidadSeleccionada(null)}
                        className="w-full text-left p-3 border border-gray-200 bg-gray-50 hover:bg-gray-100 rounded-lg transition">
                        <p className="font-medium text-gray-700">❌ Cancelar</p>
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setDuplicidadSeleccionada(dup.id)}
                      className="w-full text-white font-semibold py-2 rounded-xl hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      🔍 Revisar y Resolver
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── PESTAÑA CONFLICTOS POLÍTICOS ── */}
        {pestana === 'conflictos' && puedeConflictos && (
          <div>
            {/* Stats */}
            {statsConflictos && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Total', valor: statsConflictos.total, color: 'border-gray-400' },
                  { label: 'Pendientes', valor: statsConflictos.pendientes, color: 'border-orange-400' },
                  { label: 'En Revisión', valor: statsConflictos.en_revision, color: 'border-blue-400' },
                  { label: 'Alta Prioridad', valor: statsConflictos.alta_prioridad, color: 'border-red-400' },
                ].map((s, i) => (
                  <div key={i} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${s.color}`}>
                    <p className="text-xs text-gray-500">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-800">{s.valor || 0}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Acciones */}
            <div className="flex gap-3 mb-4 flex-wrap items-center justify-between">
              <div className="flex gap-2">
                {['', 'pendiente', 'en_revision', 'resuelto'].map((e) => (
                  <button key={e} onClick={() => setFiltroConflicto(e)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      filtroConflicto === e ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}>
                    {e === '' ? 'Activos' : e === 'pendiente' ? 'Pendientes' : e === 'en_revision' ? 'En Revisión' : 'Resueltos'}
                  </button>
                ))}
              </div>
              <button onClick={handleDetectarConflictos} disabled={detectando}
                className="text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #5c1022 100%)' }}>
                <ShieldAlert size={16} />
                {detectando ? 'Detectando...' : '🔍 Detectar Automáticamente'}
              </button>
            </div>

            {/* Lista conflictos */}
            {loadingConflictos ? (
              <p className="text-gray-500 text-center py-8">Cargando conflictos...</p>
            ) : conflictosFiltrados.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <Check className="text-green-600 mx-auto mb-2" size={32} />
                <p className="text-green-800 font-medium">✅ No hay conflictos en este filtro</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conflictosFiltrados.map((c, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-red-400">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">{c.nombre} {c.apellido_paterno}</h3>
                        <p className="text-xs text-gray-500">Tel: {c.telefono_principal} | Sección: {c.numero_seccion}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioridadColor(c.prioridad)}`}>
                          {c.prioridad}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstatusConflictoColor(c.estatus)}`}>
                          {c.estatus}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div className="bg-blue-50 p-3 rounded-lg text-xs">
                        <p className="text-blue-500 font-medium mb-1">Estructura A</p>
                        <p className="text-blue-900">{c.nombre_estructura_a || '—'}</p>
                        <p className="text-blue-700">{c.lider_a_nombre ? `${c.lider_a_nombre} ${c.lider_a_apellido}` : '—'}</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg text-xs">
                        <p className="text-purple-500 font-medium mb-1">Estructura B</p>
                        <p className="text-purple-900">{c.nombre_estructura_b || '—'}</p>
                        <p className="text-purple-700">{c.lider_b_nombre ? `${c.lider_b_nombre} ${c.lider_b_apellido}` : '—'}</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mb-3 italic">{c.descripcion}</p>

                    {c.estatus !== 'resuelto' && (
                      conflictoSeleccionado === c.id ? (
                        <div className="space-y-2">
                          <textarea value={resolucion} onChange={(e) => setResolucion(e.target.value)}
                            placeholder="Nota adicional (opcional)..."
                            rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500" />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <button onClick={() => handleResolverConflicto(c.id, 'a')}
                              disabled={!c.estructura_a_id}
                              className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed">
                              ✅ Se queda con Estructura A
                              <span className="block text-xs font-normal opacity-90">{c.nombre_estructura_a || 'No disponible'}</span>
                            </button>
                            <button onClick={() => handleResolverConflicto(c.id, 'b')}
                              disabled={!c.estructura_b_id}
                              className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 transition disabled:opacity-40 disabled:cursor-not-allowed">
                              ✅ Se queda con Estructura B
                              <span className="block text-xs font-normal opacity-90">{c.nombre_estructura_b || 'No disponible'}</span>
                            </button>
                          </div>
                          <button onClick={() => setConflictoSeleccionado(null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setConflictoSeleccionado(c.id); setResolucion(''); }}
                          className="w-full text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition"
                          style={{ background: 'linear-gradient(135deg, #dc2626 0%, #5c1022 100%)' }}>
                          🛡️ Revisar y Resolver
                        </button>
                      )
                    )}
                    {c.estatus === 'resuelto' && c.resolucion_final && (
                      <p className="text-xs text-green-700 bg-green-50 p-2 rounded-lg">
                        ✅ Resolución: {c.resolucion_final}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* MODAL SUBIR DE NIVEL */}
      {mostrarSubirNivel && dupParaSubir && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg">⬆️ Subir de Nivel</h3>
              <button onClick={() => { setMostrarSubirNivel(false); setResultadoSubida(null); }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {!resultadoSubida ? (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-5">
                    <p className="font-bold text-purple-900 text-lg">{dupParaSubir.persona}</p>
                    <p className="text-purple-700 text-sm">📱 {dupParaSubir.telefono_principal}</p>
                    <p className="text-purple-600 text-xs mt-2">
                      Este promovido aparece duplicado lo que indica un alto nivel de compromiso.
                      Al subirlo de nivel se creará su cuenta de Promotor y se agregará al Banco de Talento.
                    </p>
                  </div>
                  <div className="space-y-2 mb-5">
                    {['Se creará su usuario con rol Promotor en DOMINIUM', 'Contraseña temporal: Virtus + últimos 4 dígitos de su teléfono', 'Se agregará al Banco de Talento', 'Se resolverá la duplicidad automáticamente'].map((txt, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-green-500 mt-0.5">✅</span>
                        <span>{txt}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estatus inicial en Banco de Talento:</label>
                    <select value={estatusTalento} onChange={(e) => setEstatusTalento(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="basico">Básico</option>
                      <option value="por_ampliar">Por Ampliar</option>
                      <option value="evaluado">Evaluado</option>
                      <option value="estrategico">Estratégico</option>
                    </select>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => { setMostrarSubirNivel(false); setResultadoSubida(null); }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                      Cancelar
                    </button>
                    <button onClick={handleSubirNivel} disabled={procesando}
                      className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5c1022 100%)' }}>
                      {procesando ? 'Procesando...' : '⬆️ Confirmar Subida'}
                    </button>
                  </div>
                </>
              ) : resultadoSubida.error ? (
                <div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                    <p className="text-red-800 font-medium">❌ {resultadoSubida.error}</p>
                  </div>
                  <button onClick={() => { setMostrarSubirNivel(false); setResultadoSubida(null); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                    Cerrar
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-4 text-center">
                    <p className="text-4xl mb-2">🎉</p>
                    <p className="text-green-800 font-bold text-lg">{resultadoSubida.mensaje}</p>
                    <div className="mt-3 bg-white border border-green-200 rounded-lg p-3 text-left">
                      <p className="text-xs text-gray-500 mb-1">Contraseña temporal de acceso:</p>
                      <p className="font-mono font-bold text-green-700 text-lg">{resultadoSubida.password_temporal}</p>
                      <p className="text-xs text-gray-400 mt-1">⚠️ Comparte esta contraseña con el nuevo Promotor.</p>
                    </div>
                  </div>
                  <button onClick={() => { setMostrarSubirNivel(false); setResultadoSubida(null); }}
                    className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                    style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                    ✅ Listo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
